/**
 * ChatConnected — the digital-twin chat working IN the Home detail pane: the
 * message thread + streaming + composer for an existing session. Always has a
 * real session (this is only ever mounted bound to one — see
 * agentDetailViews.tsx / AgentDetailPane), but it may have just been minted
 * for a message typed into the agent's session-less FACE composer BEFORE this
 * component existed (Home's ChatFace) — useDeferredFirstSend's flush effect
 * picks that message up on mount via the shared deferredFirstSend store, the
 * same mechanism Session.tsx's `/sessions/new` route and every board
 * wrapper's side-channel use. This component's own composer never defers —
 * a session is always already bound here — so `send` is only ever consulted
 * for its flush side effect.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'

import { sessionKeys, type SessionDetail } from '@/hooks/useSessions'
import { useStreamingMessage } from '@/hooks/useStreamingChat'
import { useSessionEventStream } from '@/hooks/useSessionEventStream'
import { useDeferredFirstSend } from '@/hooks/useDeferredFirstSend'
import { MessageList, ChatInput, CitationSheet, StreamingMessageBubble } from '@/components/chat'
import { ChatReadingColumn } from './ChatReadingColumn'
import { usePinToBottom } from '@/hooks/usePinToBottom'
import type { CitationResponse } from '@/api/types'
import { ConversationContext } from './conversationContext'

interface ChatConnectedProps {
  session: SessionDetail
}

export function ChatConnected({ session }: ChatConnectedProps) {
  const { t } = useTranslation()
  const sessionId = session.session.id
  const streaming = useStreamingMessage(sessionId)
  const queryClient = useQueryClient()
  useSessionEventStream(sessionId, queryClient)
  // Flush-only: this instance is always bound, so `send` is never called from
  // here — only the mount-time flush effect matters (see module doc).
  useDeferredFirstSend({ sessionId, sendMessage: streaming.sendMessage })
  const [selectedCitation, setSelectedCitation] = useState<CitationResponse | null>(null)

  // The one thing that follows the conversation on this surface: the reply
  // grows in place as deltas land, and would otherwise run off the bottom
  // mid-answer.
  const { containerRef: scrollContainerRef } = usePinToBottom<HTMLDivElement>()

  // Refetch when a turn finishes so the persisted messages replace the
  // transient streaming bubble (the detail pane owns the useSession query).
  const wasStreaming = useRef(false)
  useEffect(() => {
    if (streaming.isStreaming) {
      wasStreaming.current = true
    } else if (wasStreaming.current) {
      wasStreaming.current = false
      void queryClient.invalidateQueries({ queryKey: sessionKeys.detail(sessionId) })
    }
  }, [streaming.isStreaming, sessionId, queryClient])

  const handleSend = useCallback(
    async (content: string) => {
      try {
        await streaming.sendMessage(content)
      } catch {
        // surfaced by the streaming hook
      }
    },
    [streaming],
  )


  // Suppress the streaming bubble once its final message is in the list (stale
  // error after a background refetch race).
  const streamingAlreadyRendered = useMemo(
    () =>
      !!streaming.assistantMessageId &&
      session.messages.items.some((m) => m.id === streaming.assistantMessageId),
    [streaming.assistantMessageId, session.messages],
  )

  return (
    <div className="flex h-full flex-col bg-neutral-50">
      <div ref={scrollContainerRef} className="min-h-0 flex-1 overflow-y-auto">
        <ConversationContext.Provider value={sessionId}>
          <ChatReadingColumn className="py-4">
            <MessageList
              messages={session.messages.items}
              isLoading={false}
              isPending={false}
              emptyMessage={t('session.empty')}
              onCitationClick={setSelectedCitation}
              onRetry={streaming.retry}
              isRetrying={streaming.isRetrying}
            />
            {!streamingAlreadyRendered &&
              (streaming.isStreaming || streaming.isRetrying || streaming.error) && (
                <div className="mt-3">
                  <StreamingMessageBubble
                    content={streaming.content}
                    citations={streaming.citations}
                    isStreaming={streaming.isStreaming}
                    error={streaming.error}
                    onRetry={streaming.retry}
                    isRetrying={streaming.isRetrying}
                  />
                </div>
              )}
          </ChatReadingColumn>
        </ConversationContext.Provider>
      </div>

      <div className="shrink-0 border-t border-neutral-200/50 bg-neutral-50 pb-safe">
        <ChatReadingColumn className="py-3">
          <ChatInput onSend={handleSend} isPending={streaming.isStreaming} placeholder={t('chat.placeholder')} />
        </ChatReadingColumn>
      </div>

      <CitationSheet
        citation={selectedCitation}
        open={!!selectedCitation}
        onOpenChange={(open) => !open && setSelectedCitation(null)}
      />
    </div>
  )
}
